import type { ModuleMap } from '../compile/types.ts';

const frameworkModules = {
  '@ember/application': () => import('@ember/application'),
  '@ember/application/instance': () => import('@ember/application/instance'),
  '@ember/array': () => import('@ember/array'),
  '@ember/component': () => import('@ember/component'),
  '@ember/component/helper': () => import('@ember/component/helper'),
  '@ember/component/template-only': () =>
    import('@ember/component/template-only'),
  '@ember/controller': () => import('@ember/controller'),
  '@ember/debug': () => import('@ember/debug'),
  '@ember/destroyable': () => import('@ember/destroyable'),
  '@ember/helper': () => import('@ember/helper'),
  '@ember/modifier': () => import('@ember/modifier'),
  '@ember/object': () => import('@ember/object'),
  '@ember/object/compat': () => import('@ember/object/compat'),
  '@ember/object/internals': () => import('@ember/object/internals'),
  '@ember/object/observers': () => import('@ember/object/observers'),
  '@ember/owner': () => import('@ember/owner'),
  '@ember/reactive': () => import('@ember/reactive'),
  '@ember/reactive/collections': () => import('@ember/reactive/collections'),
  '@ember/renderer': () => import('@ember/renderer'),
  '@ember/routing': () => import('@ember/routing'),
  '@ember/routing/route': () => import('@ember/routing/route'),
  '@ember/routing/router': () => import('@ember/routing/router'),
  '@ember/runloop': () => import('@ember/runloop'),
  '@ember/service': () => import('@ember/service'),
  '@ember/template': () => import('@ember/template'),
  '@ember/template-compilation': () => import('@ember/template-compilation'),
  '@ember/template-factory': () => import('@ember/template-factory'),
  '@ember/test-helpers': () => import('@ember/test-helpers'),
  '@ember/test-waiters': () => import('@ember/test-waiters'),
  '@ember/test': () => import('@ember/test'),
  '@ember/utils': () => import('@ember/utils'),
  '@ember/version': () => import('@ember/version'),
  // @ts-ignore
  '@glimmer/validator': () => import('@glimmer/validator'),
  '@glimmer/component': () => import('@glimmer/component'),
  '@glimmer/tracking': () => import('@glimmer/tracking'),
  '@glimmer/tracking/primitives/cache': () =>
    import('@glimmer/tracking/primitives/cache'),
};

const coreLibraries = {
  'ember-resolver': () => import('ember-resolver'),
  'ember-resources': () => import('ember-resources'),
  'ember-primitives': () => import('ember-primitives'),
  'repl-sdk': () => import('repl-sdk'),
};

const emberCompilationModules = {
  '@ember/template-compiler/runtime': () =>
    import('@ember/template-compiler/runtime'),
  '@ember/template-compiler': () => import('@ember/template-compiler/runtime'),
  'ember-source/ember-template-compiler/index.js': () =>
    import(
      // @ts-ignore
      'ember-source/ember-template-compiler/index.js'
    ),
  // Direct Dependencies
  '@glimdown/babel-8-lite': () => import('@glimdown/babel-8-lite'),
  'content-tag': () => import('content-tag'),
  'decorator-transforms': () => import('decorator-transforms'),
  'decorator-transforms/runtime': () => import('decorator-transforms/runtime'),
  'decorator-transforms/runtime-esm': () =>
    import('decorator-transforms/runtime-esm'),
  'decorator-transforms/globals': () => import('decorator-transforms/globals'),
  'babel-plugin-ember-template-compilation': () =>
    import('babel-plugin-ember-template-compilation'),
  // Dependencies of the above
  'babel-import-util': () => import('babel-import-util'),

  // @ts-ignore
  'babel-plugin-debug-macros': () => import('babel-plugin-debug-macros'),
  '@embroider/macros': () => ({
    // passthrough, we are not doing dead-code-elimination
    macroCondition: (x: boolean) => x,
    // I *could* actually implement this
    appEmberSatisfies: () => true,
    // I *could* actually implement this
    dependencySatisfies: () => true,
    isDevelopingApp: () => true,
    // Trying to use warp-drive in a REPL environment may be impossible, since they
    // encourage choosing your own adventure without a buildless recommended path.
    // The use of nested configs (specifically env) is also problematic for
    // "falling back to false" as what all other macros-using libraries use.
    //   (many of us have seen "Cannot access DEBUG on undefined" in build errors)
    // Even with this config, I have not successfully been able to use warp-drive
    // in any of my REPL-based projects.
    //
    // (Its also perfectly fine for warp-drive to decide they don't care about the same things I do)
    getGlobalConfig: () => ({
      WarpDrive: {
        debug: false,
        env: {
          DEBUG: false,
          TESTING: false,
          PRODUCTION: true,
        },
        activeLogging: false,
        compatWith: '99.0',
        features: {},
        deprecations: {},
        polyfillUUID: false,
        includeDataAdapter: false,
      },
    }),
    // Private

    // @ts-ignore
    importSync: (x: string) =>
      // @ts-expect-error
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      window[Symbol.for('__repl-sdk__compiler__') as any]!.resolves[x],
    moduleExists: () => false,
  }),
};

const markdownCompilationModules = {
  // Provided directly in repl-sdk, but could be overridden
  // 'rehype-raw': () => import('rehype-raw'),
  // 'rehype-stringify': () => import('rehype-stringify'),
  // 'remark-gfm': () => import('remark-gfm'),
  // 'remark-parse': () => import('remark-parse'),
  // 'remark-rehype': () => import('remark-rehype'),
  // unified: () => import('unified'),
  // 'unist-util-visit': () => import('unist-util-visit'),
};

/**
 * If any real packages are defined here, they would fallback to fetching from NPM
 * instead of loading from this pre-made bundle.
 */
export const modules = (extraModules: ModuleMap): ModuleMap => ({
  ...coreLibraries,
  ...frameworkModules,
  ...extraModules,
  ...emberCompilationModules,
  ...markdownCompilationModules,
});
